/**
 * Client service for Biomedical Waste (BMW) Management.
 * R39 C5.a2 (plan docs/plans/sector-operations.md §2 SO-P252, H§5).
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import { getApprovalsFunctions } from './approvalsApp'
import type {
  BmwCategory,
  BmwSettingsDoc,
  BmwStatusResult,
  BmwSummaryResult,
  BmwCollector,
  BmwBag,
  StartBmwHandoverInput,
  StartBmwHandoverResult,
  GetBmwHandoverInfoInput,
  GetBmwHandoverInfoResult,
  PrepareBmwHandoverOtpInput,
  PrepareBmwHandoverOtpResult,
  ConfirmBmwHandoverInput,
  ConfirmBmwHandoverResult,
  AttachBmwPickupReceiptInput,
  AttachBmwPickupReceiptResult,
} from '../types/ops'

export interface SetBmwSettingsInput {
  workspaceId: string
  storageHours?: number
  unusualKgPerCategory?: number
  requirePreTreatmentPhoto?: boolean
  collectors?: BmwCollector[]
}

export interface SaveBmwDayInput {
  workspaceId: string
  placeId: string
  dayKey: string
  weights: Record<BmwCategory, number>
  bagCounts?: Partial<Record<BmwCategory, number>>
  preTreatmentPhotos?: string[]
  bags?: BmwBag[]
  opId?: string
}

export interface SubmitBmwDayInput {
  workspaceId: string
  placeId: string
  dayKey: string
  weights: Record<BmwCategory, number>
  bagCounts?: Partial<Record<BmwCategory, number>>
  preTreatmentPhotos?: string[]
  bags?: BmwBag[]
  confirmUnusual?: boolean
  confirmBagWeightMismatch?: boolean
  opId?: string
}

export interface CorrectBmwDayInput {
  workspaceId: string
  placeId: string
  dayKey: string
  weights: Record<BmwCategory, number>
  bagCounts?: Partial<Record<BmwCategory, number>>
  reason: string
  opId?: string
}

export interface RecordBmwPickupInput {
  workspaceId: string
  placeId: string
  pickedAt: string | number
  manifestRef: string
  vehicleRef?: string
  bagCounts?: Partial<Record<BmwCategory, number>>
  opId?: string
}

export interface GetBmwStatusInput {
  workspaceId: string
  placeId: string
  dayKey?: string
}

export interface GetBmwSummaryInput {
  workspaceId: string
  placeId?: string
  year: number
  month?: number
}

export async function setBmwSettings(
  input: SetBmwSettingsInput,
): Promise<{ success: true; settings: BmwSettingsDoc }> {
  return callable<SetBmwSettingsInput, { success: true; settings: BmwSettingsDoc }>('setBmwSettings', input)
}

export async function saveBmwDay(
  input: SaveBmwDayInput,
): Promise<{ success: true; dayId: string; replayed?: boolean }> {
  return callable<SaveBmwDayInput, { success: true; dayId: string; replayed?: boolean }>('saveBmwDay', input)
}

export async function submitBmwDay(
  input: SubmitBmwDayInput,
): Promise<{ success: true; dayId: string; replayed?: boolean }> {
  return callable<SubmitBmwDayInput, { success: true; dayId: string; replayed?: boolean }>('submitBmwDay', input)
}

export async function correctBmwDay(
  input: CorrectBmwDayInput,
): Promise<{ success: true; dayId: string; replayed?: boolean }> {
  return callable<CorrectBmwDayInput, { success: true; dayId: string; replayed?: boolean }>('correctBmwDay', input)
}

export async function recordBmwPickup(
  input: RecordBmwPickupInput,
): Promise<{ success: true; pickupId: string; coveredCount: number; replayed?: boolean }> {
  return callable<RecordBmwPickupInput, { success: true; pickupId: string; coveredCount: number; replayed?: boolean }>('recordBmwPickup', input)
}

export async function getBmwStatus(
  input: GetBmwStatusInput,
): Promise<BmwStatusResult> {
  return callable<GetBmwStatusInput, BmwStatusResult>('getBmwStatus', input)
}

export async function getBmwSummary(
  input: GetBmwSummaryInput,
): Promise<BmwSummaryResult> {
  return callable<GetBmwSummaryInput, BmwSummaryResult>('getBmwSummary', input)
}

export async function startBmwHandover(
  input: StartBmwHandoverInput,
): Promise<StartBmwHandoverResult> {
  return callable<StartBmwHandoverInput, StartBmwHandoverResult>('startBmwHandover', input)
}

export async function getBmwHandoverInfo(
  input: GetBmwHandoverInfoInput,
): Promise<GetBmwHandoverInfoResult> {
  return callable<GetBmwHandoverInfoInput, GetBmwHandoverInfoResult>('getBmwHandoverInfo', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function prepareBmwHandoverOtp(
  input: PrepareBmwHandoverOtpInput,
): Promise<PrepareBmwHandoverOtpResult> {
  return callable<PrepareBmwHandoverOtpInput, PrepareBmwHandoverOtpResult>('prepareBmwHandoverOtp', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function confirmBmwHandover(
  input: ConfirmBmwHandoverInput,
): Promise<ConfirmBmwHandoverResult> {
  return callable<ConfirmBmwHandoverInput, ConfirmBmwHandoverResult>('confirmBmwHandover', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function attachBmwPickupReceipt(
  input: AttachBmwPickupReceiptInput,
): Promise<AttachBmwPickupReceiptResult> {
  return callable<AttachBmwPickupReceiptInput, AttachBmwPickupReceiptResult>('attachBmwPickupReceipt', input)
}


